<script setup>
import { computed, ref, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import ContactForm from './ContactForm.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import InboxName from 'dashboard/components/widgets/InboxName.vue';
import ScheduledMessages from '../scheduledMessages/ScheduledMessages.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  contact: { type: Object, default: () => ({}) },
  conversationId: { type: [Number, String], default: null },
  inboxId: { type: [Number, String], default: null },
});

const emit = defineEmits(['cancel']);

const store = useStore();
const uiFlags = useMapGetter('contacts/getUIFlags');
const inboxes = useMapGetter('inboxes/getInboxes');

const contactForm = ref(null);
const confirmDiscardDialog = ref(null);

const linkedInboxIds = computed(() => {
  const contactInboxes =
    props.contact.contact_inboxes || props.contact.contactInboxes || [];
  const ids = new Set(
    contactInboxes
      .map(item => Number(item.inbox_id ?? item.inboxId))
      .filter(Number.isFinite)
  );
  const currentInboxId = Number(props.inboxId);
  if (Number.isFinite(currentInboxId) && currentInboxId > 0) {
    ids.add(currentInboxId);
  }
  return ids;
});

const linkedInboxes = computed(() =>
  inboxes.value.filter(inbox => linkedInboxIds.value.has(Number(inbox.id)))
);

const onCancel = async () => {
  const hasChanges = contactForm.value?.hasUnsavedChanges;
  if (hasChanges) {
    const shouldDiscard = await confirmDiscardDialog.value?.showConfirmation();
    if (!shouldDiscard) return;
  }
  emit('cancel');
};

const onSubmit = async contactItem => {
  await store.dispatch('contacts/update', contactItem);
  await store.dispatch('contacts/fetchContactableInbox', props.contact.id);
};

watch(
  () => props.show,
  isVisible => {
    if (isVisible && props.contact.id) {
      store.dispatch('contacts/fetchContactableInbox', props.contact.id);
    }
  }
);

useKeyboardEvents({
  Escape: {
    action: () => {
      if (props.show) onCancel();
    },
    allowOnFocusedInput: true,
  },
});
</script>

<template>
  <transition
    enter-active-class="transition duration-200 ease-out"
    enter-from-class="opacity-0 scale-[0.98]"
    leave-active-class="transition duration-150 ease-in"
    leave-to-class="opacity-0 scale-[0.98]"
  >
    <div
      v-if="show"
      class="fixed inset-0 z-[9980] flex items-center justify-center bg-n-alpha-black2 backdrop-blur-[4px] p-4"
      @mousedown.self="onCancel"
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="contact-details-title"
        class="flex w-full max-w-6xl max-h-[calc(100vh-2rem)] flex-col overflow-hidden rounded-lg border border-n-weak bg-n-surface-2 shadow-xl"
      >
        <header
          class="flex flex-shrink-0 items-center justify-between gap-4 border-b border-n-weak px-6 py-4"
        >
          <div class="min-w-0">
            <p class="mb-0.5 text-xs font-medium uppercase text-n-slate-10">
              {{ $t('EDIT_CONTACT.TITLE') }}
            </p>
            <h2
              id="contact-details-title"
              class="m-0 truncate text-lg font-medium text-n-slate-12"
            >
              {{
                contact.name ||
                contact.email ||
                $t('CONTACT_PANEL.NOT_AVAILABLE')
              }}
            </h2>
          </div>
          <Button
            v-tooltip.left="$t('GENERAL.CLOSE')"
            :aria-label="$t('GENERAL.CLOSE')"
            icon="i-lucide-x"
            slate
            ghost
            sm
            @click="onCancel"
          />
        </header>

        <div
          class="flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto lg:grid lg:grid-cols-[minmax(0,1fr)_22rem] lg:overflow-hidden"
        >
          <div class="w-full min-w-0 flex-none lg:min-h-0 lg:overflow-y-auto">
            <ContactForm
              ref="contactForm"
              :contact="contact"
              :in-progress="uiFlags.isUpdating"
              :on-submit="onSubmit"
              @success="$emit('cancel')"
              @cancel="onCancel"
            />
          </div>

          <aside
            class="w-full min-w-0 flex-none border-t border-n-weak bg-n-surface-1 lg:min-h-0 lg:overflow-y-auto lg:border-l lg:border-t-0"
          >
            <section class="border-b border-n-weak px-5 py-5">
              <h3 class="mb-3 text-sm font-medium text-n-slate-12">
                {{ $t('SIDEBAR.CHANNELS') }}
              </h3>
              <p
                v-if="uiFlags.isFetchingInboxes"
                class="m-0 text-sm text-n-slate-11"
              >
                {{ $t('SEARCH.LOADING_DATA') }}
              </p>
              <div v-else-if="linkedInboxes.length" class="flex flex-col gap-2">
                <InboxName
                  v-for="inbox in linkedInboxes"
                  :key="inbox.id"
                  :inbox="inbox"
                  class="rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2"
                />
              </div>
              <p v-else class="m-0 text-sm text-n-slate-11">
                {{ $t('SIDEBAR.NO_ITEMS') }}
              </p>
            </section>

            <section class="py-2">
              <h3 class="mb-0 px-5 pt-3 text-sm font-medium text-n-slate-12">
                {{ $t('CONVERSATION_SIDEBAR.ACCORDION.SCHEDULED_MESSAGES') }}
              </h3>
              <ScheduledMessages
                v-if="conversationId"
                :conversation-id="conversationId"
                :inbox-id="inboxId"
              />
              <p v-else class="m-0 px-5 py-5 text-sm text-n-slate-11">
                {{ $t('SCHEDULED_MESSAGES.EMPTY_STATE') }}
              </p>
            </section>
          </aside>
        </div>
      </section>
    </div>
  </transition>
  <woot-confirm-modal
    ref="confirmDiscardDialog"
    :title="$t('EDIT_CONTACT.CONFIRM_DISCARD.TITLE')"
    :description="$t('EDIT_CONTACT.CONFIRM_DISCARD.MESSAGE')"
    :confirm-label="$t('EDIT_CONTACT.CONFIRM_DISCARD.YES')"
    :cancel-label="$t('EDIT_CONTACT.CONFIRM_DISCARD.NO')"
  />
</template>
